import React, { useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import RealtimeBoard from "react-trello";
import {
  Badge,
  Dropdown,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Avatar from "../../../../components/Avatar";
import { DivCard, KanbanGlobalStyle } from "./styles";

interface PipelineKanbanProps {
  lanes: any[];
  onMoverCard: (cardId: string, sourceLaneId: string, targetLaneId: string) => void;
  onCardClick: (card: any) => void;
  onAddCard?: (laneId: string) => void;
  onChangeSituacao?: (cardId: string, novaSituacao: string) => void;
  onAddLane?: () => void;
  onVerEntidade?: (tipo: string, id: number) => void;
  onCriarCardFromCard?: (card: any) => void;
  loading?: boolean;
}

const SITUACAO_LABELS: Record<string, string> = {
  aberto: "Aberto",
  concluido: "Concluído",
  cancelado: "Cancelado",
  arquivado: "Arquivado",
};

const SITUACAO_COLORS: Record<string, string> = {
  aberto: "info",
  concluido: "success",
  cancelado: "danger",
  arquivado: "secondary",
};

const PipelineKanban: React.FC<PipelineKanbanProps> = ({ lanes, onMoverCard, onCardClick, onAddCard, onChangeSituacao, onAddLane, onVerEntidade, onCriarCardFromCard, loading }) => {
  const boardWrapperRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollInnerRef = useRef<HTMLDivElement>(null);
  const isSyncingFromBoard = useRef(false);
  const isSyncingFromSticky = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [lanesContainerEl, setLanesContainerEl] = useState<Element | null>(null);

  // Sticky scrollbar sync
  useEffect(() => {
    const timeout = setTimeout(() => {
      const boardEl = boardWrapperRef.current?.firstElementChild as HTMLElement;
      if (!boardEl || !stickyScrollRef.current || !stickyScrollInnerRef.current) return;

      const updateWidth = () => {
        if (!stickyScrollRef.current || !stickyScrollInnerRef.current) return;
        const boardScrollRange = boardEl.scrollWidth - boardEl.clientWidth;
        stickyScrollInnerRef.current.style.width =
          (boardScrollRange + stickyScrollRef.current.clientWidth) + "px";
      };

      const onBoardScroll = () => {
        if (isSyncingFromSticky.current) return;
        isSyncingFromBoard.current = true;
        if (stickyScrollRef.current) {
          stickyScrollRef.current.scrollLeft = boardEl.scrollLeft;
        }
        isSyncingFromBoard.current = false;
      };

      const onStickyScroll = () => {
        if (isSyncingFromBoard.current) return;
        isSyncingFromSticky.current = true;
        if (stickyScrollRef.current) {
          boardEl.scrollLeft = stickyScrollRef.current.scrollLeft;
        }
        isSyncingFromSticky.current = false;
      };

      updateWidth();
      boardEl.addEventListener("scroll", onBoardScroll);
      stickyScrollRef.current.addEventListener("scroll", onStickyScroll);

      const observer = new ResizeObserver(updateWidth);
      observer.observe(boardEl);

      const stickyEl = stickyScrollRef.current;
      return () => {
        boardEl.removeEventListener("scroll", onBoardScroll);
        if (stickyEl) stickyEl.removeEventListener("scroll", onStickyScroll);
        observer.disconnect();
      };
    }, 400);

    return () => clearTimeout(timeout);
  }, [lanes]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const boardEl = boardWrapperRef.current?.firstElementChild as HTMLElement;
      if (boardEl) {
        setLanesContainerEl(boardEl);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [lanes]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (cardId: string, sourceLaneId: string, targetLaneId: string) => {
    setIsDragging(false);
    if (sourceLaneId !== targetLaneId) {
      onMoverCard(cardId, sourceLaneId, targetLaneId);
    }
  };

  const handleCardClick = (cardId: string, metadata: any, laneId: string) => {
    if (onCardClick) {
      const lane = lanes.find(l => l.id === laneId);
      const card = lane?.cards.find((c: any) => c.id === cardId);
      if (card) onCardClick(card);
    }
  };

  const CustomLaneHeader = ({ title, barColor }: any) => {
    return (
      <div
        style={{
          boxShadow: "none",
          border: "none",
          backgroundColor: "transparent",
          padding: "10px",
        }}
      >
        <div
          style={{
            height: 5,
            marginBottom: 5,
            backgroundColor: barColor,
            borderRadius: 3,
          }}
        ></div>

        <header
          style={{
            paddingBottom: 6,
            marginBottom: 10,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            fontSize: 14,
            fontWeight: "bold",
          }}
        >
          {title}
        </header>
      </div>
    );
  };

  const CustomLaneFooter = (props: any) => {
    return (
      <div
        onClick={() => onAddCard && onAddCard(props.laneId)}
        style={{
          padding: "8px 10px",
          marginTop: "8px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#2c5fd9",
          fontSize: "13px",
          fontWeight: "bold",
          borderRadius: "8px",
          transition: "all 0.15s ease-in-out",
          backgroundColor: "transparent",
          border: "2px solid #2c5fd9",
          boxShadow: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#2c5fd9";
          e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "#2c5fd9";
        }}
      >
        <i className="ni ni-fat-add mr-1" style={{ fontSize: "1.2rem" }}></i>
        Adicionar card
      </div>
    );
  };

  const prioridadeCores: Record<string, string> = {
    baixa: "#adb5bd",
    normal: "#5e72e4",
    alta: "#fb6340",
    urgente: "#f5365c",
  };

  const ENTIDADE_URLS: Record<string, (id: number) => string> = {
    oportunidade: (id) => `${window.location.origin}/admin/oportunidades/${id}/edit`,
    cliente: (id) => `${window.location.origin}/admin/clientes/${id}/edit`,
    card: (id) => `${window.location.origin}/admin/cards/${id}/edit`,
  };

  const ENTIDADE_LABELS: Record<string, string> = {
    oportunidade: "Ver oportunidade",
    cliente: "Ver cliente",
    card: "Ver card",
  };

  const CustomCard = (props: any) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const toggleMenu = () => setMenuOpen(prev => !prev);

    const entidadeUrl = props.entidade_tipo && props.entidade_id
      ? ENTIDADE_URLS[props.entidade_tipo]?.(props.entidade_id)
      : null;

    return (
      <DivCard {...props}>
        <header
          style={{
            borderBottom: "1px solid #eee",
            paddingBottom: 6,
            marginBottom: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            color: "#344767",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: 0,
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {props.title}
            </div>
            {entidadeUrl && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onVerEntidade) onVerEntidade(props.entidade_tipo, props.entidade_id);
                }}
                style={{
                  fontSize: "11px",
                  color: "#2c5fd9",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "3px",
                  width: "fit-content",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
              >
                <i className="fas fa-external-link-alt" style={{ fontSize: "9px" }} />
                {ENTIDADE_LABELS[props.entidade_tipo] ?? "Ver entidade"}
              </a>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {props.prioridade && (
              <Badge
                style={{
                  backgroundColor: prioridadeCores[props.prioridade] || "#5e72e4",
                  color: "#fff",
                  fontSize: "10px",
                  padding: "3px 8px",
                  textTransform: "uppercase",
                }}
                pill
              >
                {props.prioridade}
              </Badge>
            )}
            {menuOpen && (
              <div
                style={{
                  position: "fixed",
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                  zIndex: 1040,
                }}
                onClick={(e) => { e.stopPropagation(); toggleMenu(); }}
              />
            )}
            <Dropdown
              isOpen={menuOpen}
              toggle={toggleMenu}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ position: "relative", zIndex: menuOpen ? 1041 : "auto" }}
            >
              <DropdownToggle
                tag="button"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 6px",
                  color: "#8898aa",
                  fontSize: "16px",
                  lineHeight: 1,
                }}
              >
                <i className="fas fa-ellipsis-v" />
              </DropdownToggle>
              <DropdownMenu
                strategy="fixed"
                positionFixed={true}
                modifiers={{ flip: { enabled: false }, preventOverflow: { boundariesElement: 'viewport' } }}
              >
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onCriarCardFromCard && onCriarCardFromCard(props);
                    toggleMenu();
                  }}
                >
                  <i className="fas fa-plus mr-2" />
                  Criar card a partir deste
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </header>

        <div
          style={{
            fontSize: 14,
            color: "#4C4C4C",
            display: "flex",
            flexDirection: "column",
            padding: ".5rem",
            gap: ".5rem",
            flex: 1,
            justifyContent: "space-between"
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {props.data_inicio && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13px",
                  color: "#666",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <i className="fas fa-play-circle" style={{ fontSize: "16px" }}></i>
                </div>
                <span style={{ marginLeft: "5px" }}>
                  Início: {new Date(props.data_inicio).toLocaleDateString("pt-BR")}
                </span>
              </div>
            )}

            {props.data_prazo && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13px",
                  color: "#666",
                }}
              >
                <div
                  style={{
                    width: "25px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <i className="far fa-calendar-alt" style={{ fontSize: "16px" }}></i>
                </div>
                <span style={{ marginLeft: "5px" }}>
                  Prazo: {new Date(props.data_prazo).toLocaleDateString("pt-BR")}
                </span>
              </div>
            )}

          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
              }}
            >
              <UncontrolledDropdown
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                direction="down"
              >
                <DropdownToggle
                  tag="span"
                  data-toggle="dropdown"
                  style={{ cursor: "pointer" }}
                >
                  <Badge
                    color={SITUACAO_COLORS[props.situacao] || "info"}
                    pill
                    style={{
                      fontSize: "10px",
                      padding: "4px 8px",
                      textTransform: "uppercase",
                      lineHeight: "1.2",
                    }}
                  >
                    {SITUACAO_LABELS[props.situacao] || "Aberto"}{" "}
                    <i className="fas fa-caret-down"></i>
                  </Badge>
                </DropdownToggle>
                <DropdownMenu strategy="fixed" positionFixed={true}>
                  {Object.keys(SITUACAO_LABELS)
                    .filter((s) => s !== (props.situacao || "aberto"))
                    .map((s) => (
                      <DropdownItem
                        key={s}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onChangeSituacao) onChangeSituacao(props.id, s);
                        }}
                      >
                        {SITUACAO_LABELS[s]}
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </UncontrolledDropdown>

              {props.tags && (() => {
                const all = props.tags.split(",").map((t: string) => t.trim()).filter(Boolean);
                const visible = all.slice(0, 3);
                const extra = all.length - visible.length;
                return (
                  <>
                    {visible.map((tag: string) => (
                      <span
                        key={tag}
                        title={tag}
                        style={{
                          fontSize: "10px",
                          padding: "2px 7px",
                          borderRadius: "20px",
                          backgroundColor: "#eef2ff",
                          color: "#4c6ef5",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          maxWidth: "90px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                    {extra > 0 && (
                      <span
                        title={all.slice(3).join(", ")}
                        style={{
                          fontSize: "10px",
                          padding: "2px 7px",
                          borderRadius: "20px",
                          backgroundColor: "#f1f3f5",
                          color: "#868e96",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        +{extra}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>

            {(() => {
              const avatarStyle = {
                width: "25px", height: "25px", minWidth: "25px",
                fontSize: "10px", border: '2px solid #fff', marginRight: '-10px'
              };
              const todos = [
                ...(props.criador ? [{ key: 'criador', user: props.criador }] : []),
                ...(props.membros || [])
                  .filter((m: any) => m.user?.id !== props.criador?.id)
                  .map((m: any, i: number) => ({ key: `m-${m.id}-${i}`, user: m.user })),
              ];
              const visible = todos.slice(0, 3);
              const extra = todos.length - visible.length;
              return (
                <div style={{ display: 'flex', flexDirection: 'row-reverse', flexShrink: 0 }}>
                  {extra > 0 && (
                    <div style={{
                      ...avatarStyle,
                      borderRadius: '50%', backgroundColor: '#e9ecef',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#8898aa', fontWeight: 'bold',
                    }}>
                      +{extra}
                    </div>
                  )}
                  {visible.map(({ key, user }) => (
                    <Avatar key={key} className="rounded-circle" style={avatarStyle} user={user} />
                  ))}
                </div>
              );
            })()}
          </div>

        </div>
      </DivCard>
    );
  };

  const components = {
    LaneHeader: CustomLaneHeader,
    AddCardLink: CustomLaneFooter,
    Card: CustomCard,
  };

  return (
    <>
      <KanbanGlobalStyle />
      <div
        ref={boardWrapperRef}
        className={`kanban-board-wrapper${isDragging ? " is-dragging" : ""}`}
      >
        <RealtimeBoard
          components={components}
          editable={true}
          canAddLanes={false}
          data={{ lanes }}
          handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd}
          onCardClick={handleCardClick}
          hideCardDeleteIcon={true}
          laneStyle={{
            width: 300,
            minWidth: 300,
            backgroundColor: "#f4f7fa",
            padding: "8px",
            paddingBottom: "8px",
            borderRadius: "8px",
            margin: "0 10px",
            height: "75vh",
            maxHeight: "75vh",
          }}
          style={{
            fontFamily: "inherit",
            backgroundColor: "#ffffff",
            height: "auto",
            paddingLeft: 0,
          }}
          tagStyle={{
            fontSize: "85%",
          }}
        />
      </div>

      {lanesContainerEl && onAddLane && ReactDOM.createPortal(
        <div
          onClick={onAddLane}
          style={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: 300,
            minWidth: 300,
            height: "75vh",
            maxHeight: "75vh",
            flexShrink: 0,
            backgroundColor: "transparent",
            border: "2px dashed #dee2e6",
            borderRadius: "8px",
            margin: "0 10px",
            cursor: "pointer",
            color: "#adb5bd",
            gap: "10px",
            transition: "color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#2c5fd9";
            e.currentTarget.style.color = "#2c5fd9";
            e.currentTarget.style.backgroundColor = "rgba(44, 95, 217, 0.04)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#dee2e6";
            e.currentTarget.style.color = "#adb5bd";
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <i className="ni ni-fat-add" style={{ fontSize: "2.5rem" }} />
          <span style={{ fontSize: "13px", fontWeight: 600 }}>Novo estágio</span>
        </div>,
        lanesContainerEl
      )}

      <div
        ref={stickyScrollRef}
        className="kanban-sticky-scrollbar"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          overflowX: "auto",
          overflowY: "hidden",
          height: "20px",
          zIndex: 1000,
          backgroundColor: "transparent",
        }}
      >
        <div ref={stickyScrollInnerRef} style={{ height: "1px" }} />
      </div>
    </>
  );
};

export default PipelineKanban;
